import { useState, useCallback } from 'react'
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Uploader } from '@/components/Uploader'
import { useAuth } from '@/hooks/useAuth'
import { broadcastDeviceBannerService } from '@/services/broadcast_device_banner_service'
import { toast } from 'sonner'
import { useBroadcastDeviceBanners } from '@/hooks/useBroadcastDeviceBanners'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-react'
import { BroadcastDeviceBanner } from '@magnetar/data-access'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

type BannerSettingProps = {
  broadcastDeviceId?: number
  isLoading?: boolean
}

export function BannerSetting({ broadcastDeviceId, isLoading }: BannerSettingProps) {
  const { token } = useAuth()
  const { banners, mutate } = useBroadcastDeviceBanners(broadcastDeviceId?.toString())
  const [selectedBannerId, setSelectedBannerId] = useState<number | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [uploaderKey, setUploaderKey] = useState(0)
  const [stayTimeDialogOpen, setStayTimeDialogOpen] = useState(false)
  const [stayTime, setStayTime] = useState('')

  const handleUploadSuccess = async (result: { uuid: string }) => {
    if (!broadcastDeviceId || !token) return

    const { uuid } = result
    const response = await broadcastDeviceBannerService.createBanner(
      broadcastDeviceId,
      {
        broadcast_device_id: broadcastDeviceId,
        file_uuid: uuid,
        is_active: true,
        stay_time: 10000,
      },
      token,
    )

    if (response.success) {
      toast.success('橫幅圖片已成功上傳')
      mutate()
      setUploaderKey((prev) => prev + 1)
    } else {
      toast.error('橫幅圖片上傳失敗，請稍後再試')
    }
  }

  const handleDelete = async () => {
    if (!broadcastDeviceId || !selectedBannerId || !token) return

    const response = await broadcastDeviceBannerService.deleteBanner(
      broadcastDeviceId,
      selectedBannerId,
      token,
    )

    if (response.success) {
      toast.success('橫幅圖片已成功刪除')
      mutate()
    } else {
      toast.error('橫幅圖片刪除失敗，請稍後再試')
    }

    setShowDeleteDialog(false)
    setSelectedBannerId(null)
  }

  const handleStayTimeClick = useCallback((bannerId: number, currentStayTime?: number) => {
    setSelectedBannerId(bannerId)
    setStayTime(currentStayTime ? String(Math.round(currentStayTime / 1000)) : '5')
    setStayTimeDialogOpen(true)
  }, [])

  const handleStayTimeSubmit = useCallback(async () => {
    if (!selectedBannerId || !token) return

    try {
      const stayTimeMs = parseInt(stayTime) * 1000
      const response = await broadcastDeviceBannerService.updateBannerStayTime(
        selectedBannerId,
        stayTimeMs,
        token,
      )

      if (response.success) {
        toast.success('已成功更新停留時間')
        mutate()
        setStayTimeDialogOpen(false)
        setSelectedBannerId(null)
        setStayTime('')
      } else {
        toast.error(response.error?.message || '更新停留時間失敗')
      }
    } catch {
      toast.error('更新停留時間失敗')
    }
  }, [selectedBannerId, token, stayTime, mutate])

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
  )

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !broadcastDeviceId || !token) return

    const oldIndex = banners.findIndex(
      (banner) => banner.broadcast_device_banner_id === Number(active.id),
    )
    const newIndex = banners.findIndex(
      (banner) => banner.broadcast_device_banner_id === Number(over.id),
    )

    if (oldIndex === -1 || newIndex === -1) return

    const updatedBanners = arrayMove(banners, oldIndex, newIndex)

    // 樂觀更新 UI
    mutate({ success: true, data: updatedBanners }, false)

    try {
      const response = await broadcastDeviceBannerService.updateBannerOrder(
        broadcastDeviceId,
        updatedBanners.map((banner) => banner.broadcast_device_banner_id),
        token,
      )

      if (response.success) {
        toast.success('橫幅順序已更新')
        mutate()
      } else {
        toast.error(response.error?.message || '更新橫幅順序失敗')
        mutate()
      }
    } catch {
      toast.error('更新橫幅順序失敗')
      mutate()
    }
  }

  if (isLoading) {
    return <div>載入中...</div>
  }

  if (!broadcastDeviceId) return null

  return (
    <div className="space-y-6">
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight">橫幅圖片設定</h2>

      <div className="space-y-6">
        <div className="flex items-start">
          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative rounded-lg border bg-gray-100 touch-none aspect-square">
                <Uploader
                  key={uploaderKey}
                  folder="banners"
                  onUploadSuccess={handleUploadSuccess}
                  allowedFileTypes={['.jpg', '.jpeg', '.png', '.webp']}
                  className="absolute h-full w-full"
                />
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={banners.map((b) => b.broadcast_device_banner_id)}
                  strategy={rectSortingStrategy}
                >
                  {banners.map((banner) => (
                    <SortableBannerItem
                      key={banner.broadcast_device_banner_id}
                      banner={banner}
                      onDelete={() => {
                        setSelectedBannerId(banner.broadcast_device_banner_id)
                        setShowDeleteDialog(true)
                      }}
                      onStayTimeClick={() =>
                        handleStayTimeClick(banner.broadcast_device_banner_id, banner.stay_time)
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除這張圖片嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原，刪除後圖片將從輪播列表中移除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={stayTimeDialogOpen} onOpenChange={setStayTimeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>設定停留時間</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="stayTime" className="text-right">
                停留時間
              </Label>
              <Input
                id="stayTime"
                type="number"
                min="1"
                className="col-span-3"
                value={stayTime}
                onChange={(e) => setStayTime(e.target.value)}
                placeholder="請輸入秒數"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStayTimeDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleStayTimeSubmit}>確認</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

type SortableBannerItemProps = {
  banner: BroadcastDeviceBanner
  onDelete: () => void
  onStayTimeClick: () => void
}

function SortableBannerItem({ banner, onDelete, onStayTimeClick }: SortableBannerItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: banner.broadcast_device_banner_id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="group relative rounded-lg border bg-gray-100 touch-none aspect-square"
    >
      <img
        src={`${import.meta.env.VITE_API_URL}/api/files/download/${banner.file_uuid}`}
        alt="Banner"
        className="h-full w-full object-cover object-center"
      />
      <Button
        variant="destructive"
        size="icon"
        className="absolute right-2 top-2 flex"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <Badge
        variant="secondary"
        className="absolute bottom-2 right-2 cursor-pointer hover:bg-secondary/80"
        onClick={(e) => {
          e.stopPropagation()
          onStayTimeClick()
        }}
      >
        {banner.stay_time ? `${Math.round(banner.stay_time / 1000)}秒` : '無設定秒數'}
      </Badge>
    </div>
  )
}
